import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Target, Upload, Users, Wrench, Activity, ShieldCheck, Play, Check, RotateCcw, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/api';
import { toast } from 'sonner';
import { EvidenceQualityChip, formatPeriod } from './RoeLeanOverview';

const LEVER_ICON = { manager_observation: Users, manager_support: Users, environment: Wrench, operational: Activity };
const LEVER_LABEL = { manager_observation: 'Manager lever', manager_support: 'Manager lever', environment: 'Work environment', operational: 'Operational signal' };
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '');

// Tiny sparkline of the monthly Application gap since a tactic started.
function GapSparkline({ points, width = 120, height = 28 }) {
  const vals = points.map((p) => p.app_gap).filter((v) => typeof v === 'number');
  if (vals.length < 2) return null;
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const step = width / (vals.length - 1);
  const d = vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(height - 3 - ((v - min) / span) * (height - 6)).toFixed(1)}`).join(' ');
  const improving = vals[vals.length - 1] < vals[0];
  return (
    <svg width={width} height={height} className="shrink-0" data-testid="gap-sparkline" aria-label="Application gap since start">
      <path d={d} fill="none" stroke={improving ? '#059669' : '#e11d48'} strokeWidth="2" strokeLinecap="round" />
      <circle cx={(vals.length - 1) * step} cy={height - 3 - ((vals[vals.length - 1] - min) / span) * (height - 6)} r="2.5" fill={improving ? '#059669' : '#e11d48'} />
    </svg>
  );
}

function TacticStatus({ tactic, status, currentGap, trend, onChange, busy }) {
  const s = status?.status || 'not_started';
  if (s === 'not_started') {
    return (
      <button onClick={() => onChange(tactic, 'in_progress')} disabled={busy} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-slate-900 border border-slate-300 rounded-full px-2 py-0.5 bg-white hover:bg-slate-50 transition-colors disabled:opacity-50" data-testid={`tactic-start-${tactic.key}`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Mark in progress
      </button>
    );
  }
  const startGap = typeof status.gap_at_start === 'number' ? status.gap_at_start : null;
  const nowGap = s === 'done' && typeof status.gap_at_done === 'number' ? status.gap_at_done : currentGap;
  const delta = startGap != null && nowGap != null ? Math.round((nowGap - startGap) * 10) / 10 : null;
  const startMonth = (status.started_at || '').slice(0, 7);
  // Include the last month before the start as the baseline point.
  const all = (trend || []).filter((p) => p.period && typeof p.app_gap === 'number');
  const firstIdx = all.findIndex((p) => p.period >= startMonth);
  const since = firstIdx === -1 ? all.slice(-1) : all.slice(Math.max(0, firstIdx - 1));
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`tactic-status-${tactic.key}`}>
      <span className={cn('inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full border', s === 'done' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-sky-50 text-sky-700 border-sky-200')}>
        {s === 'done' ? <Check className="h-3 w-3" /> : <Play className="h-3 w-3" />}{s === 'done' ? 'Done' : 'In progress'}
      </span>
      {startGap != null && (
        <span className="text-[11px] text-slate-600" data-testid={`tactic-since-${tactic.key}`}>
          Since {fmtDay(status.started_at)}: gap {Math.round(startGap)} → {nowGap != null ? Math.round(nowGap) : '—'}
          {delta != null && <span className={cn('font-semibold ml-1', delta < 0 ? 'text-emerald-700' : delta > 0 ? 'text-rose-700' : 'text-slate-500')}>({delta > 0 ? '+' : ''}{delta} pts)</span>}
        </span>
      )}
      {since.length >= 2 && <GapSparkline points={since} />}
      {since.length >= 2 && <span className="text-[10px] text-slate-400">{formatPeriod(since[0].period)} → {formatPeriod(since[since.length - 1].period)}</span>}
      {s === 'in_progress' && (
        <button onClick={() => onChange(tactic, 'done')} disabled={busy} className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 transition-colors disabled:opacity-50" data-testid={`tactic-done-${tactic.key}`}><Check className="h-3 w-3" /> Mark done</button>
      )}
      <button onClick={() => onChange(tactic, 'not_started')} disabled={busy} title="Reset" className="text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-50" data-testid={`tactic-reset-${tactic.key}`}><RotateCcw className="h-3 w-3" /></button>
    </div>
  );
}

// Priorities-view card: turns a material Application Transfer Gap into a
// concrete coaching plan with per-org follow-through on each tactic.
export default function GapCoachingCard({ detail, navigate, organizationId, trend }) {
  const coaching = detail?.coaching;
  const [statuses, setStatuses] = useState({});
  const [busy, setBusy] = useState(null);
  const orgQ = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : '';

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/roe/actuals/coaching/tactics${orgQ}`);
      setStatuses(Object.fromEntries((res.data.tactics || []).map((t) => [t.key, t])));
    } catch { /* keep last */ }
  }, [orgQ]);
  useEffect(() => { if (coaching) load(); }, [coaching, load]);

  const change = async (tactic, status) => {
    setBusy(tactic.key);
    try {
      const body = { status, title: tactic.title, gap_at_start: coaching.gap };
      if (organizationId) body.organization_id = organizationId;
      await api.put(`/roe/actuals/coaching/tactics/${tactic.key}`, body);
      toast.success(status === 'in_progress' ? `"${tactic.title}" marked in progress` : status === 'done' ? `"${tactic.title}" marked done` : 'Tactic reset');
      await load();
    } catch (err) { toast.error(`Could not update: ${err.response?.data?.detail || err.message}`); }
    finally { setBusy(null); }
  };

  if (!coaching) return null;
  const critical = coaching.severity === 'Critical';
  const active = Object.values(statuses).filter((s) => s.status === 'in_progress').length;
  const done = Object.values(statuses).filter((s) => s.status === 'done').length;
  return (
    <div className={cn('rounded-2xl border p-5 bg-white shadow-sm', critical ? 'border-rose-200' : 'border-amber-200')} data-testid="gap-coaching-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <span className={cn('mt-0.5 w-9 h-9 rounded-xl flex items-center justify-center shrink-0', critical ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-600')}><Target className="h-5 w-5" /></span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Application Transfer Gap</p>
              <span className={cn('text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full border', critical ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-800 border-amber-200')} data-testid="gap-coaching-severity">{coaching.severity}</span>
              <EvidenceQualityChip quality={detail.evidence_quality} compact />
              {(active > 0 || done > 0) && <span className="text-[10px] font-semibold text-slate-500" data-testid="gap-coaching-progress">{active} in progress · {done} done</span>}
            </div>
            <h3 className="text-base font-bold text-slate-900 mt-1" data-testid="gap-coaching-headline">{coaching.headline}</h3>
            <p className="text-sm text-slate-600 mt-1">{coaching.why}</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className={cn('text-3xl font-extrabold tabular-nums leading-none', critical ? 'text-rose-600' : 'text-amber-600')} data-testid="gap-coaching-gap">-{Math.round(coaching.gap)}<span className="text-base font-bold ml-0.5">pts</span></p>
          <p className="text-[11px] text-slate-500 mt-1">potential {Math.round(coaching.potential)}% → actual {Math.round(coaching.actual)}%</p>
        </div>
      </div>

      <ol className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2.5" data-testid="gap-coaching-tactics">
        {coaching.tactics.map((t, i) => {
          const Icon = LEVER_ICON[t.lever] || ShieldCheck;
          const st = statuses[t.key];
          return (
            <li key={t.key || i} className={cn('flex gap-3 rounded-xl border p-3', st?.status === 'done' ? 'border-emerald-200 bg-emerald-50/40' : st?.status === 'in_progress' ? 'border-sky-200 bg-sky-50/40' : 'border-slate-200 bg-slate-50')} data-testid={`gap-coaching-tactic-${i}`}>
              <span className="w-6 h-6 rounded-full bg-white border border-slate-200 text-[11px] font-bold text-slate-700 flex items-center justify-center shrink-0">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800 leading-tight">{t.title}</p>
                <p className="text-xs text-slate-600 mt-0.5">{t.detail}</p>
                <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
                  <p className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500"><Icon className="h-3 w-3" />{LEVER_LABEL[t.lever] || 'Practice'}</p>
                  <TacticStatus tactic={t} status={st} currentGap={coaching.gap} trend={trend} onChange={change} busy={busy === t.key} />
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button onClick={() => navigate('/roe-health/deep-dives')} className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 transition-colors" data-testid="gap-coaching-import-btn"><Upload className="h-3.5 w-3.5" /> Import application evidence</button>
        <button onClick={() => navigate('/roe-health')} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors" data-testid="gap-coaching-overview-btn">View transfer gap <ArrowRight className="h-3.5 w-3.5" /></button>
        {coaching.weakest_source && <span className="ml-auto text-[11px] text-slate-500">Thinnest evidence: <span className="font-medium text-slate-700">{coaching.weakest_source.replace('_', ' ')}</span></span>}
      </div>
    </div>
  );
}
